import { type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { type OfferV11 } from '@redteclab/api/clients/bully'

import {
  OFFER_PROMOTION_TYPES,
  type OfferV1OfferPromotionPromotionType,
} from '../../../model/OfferV1OfferPromotionPromotionType'
import { productAccessorGetOfferPromotionType } from '../../../model/productAccessor/productAccessorGetOfferPromotionType'
import { useProductPageContext } from '../../productPageContext'

interface ProductPromotion {
  hasPromotion: boolean
  promotionDescription: ReactNode
  promotionFooter: ReactNode
  promotionTitle: ReactNode
  promotionType: OfferV1OfferPromotionPromotionType | undefined
}

const getPromotionFooter = (): ReactNode => {
  return <FormattedMessage id="general.label.whileStockLasts" />
}

const getPromotionTitle = (
  currentOffer: OfferV11,
  promotionType: OfferV1OfferPromotionPromotionType,
): ReactNode => {
  if (promotionType === OFFER_PROMOTION_TYPES.FREEBIE) {
    return <FormattedMessage id="product.promotion.content.headline.freebie" />
  }

  return currentOffer.offerPromotion?.promotionOfferPostfix
}

const getPromotionDescription = (offer: OfferV11): ReactNode => {
  return offer.offerPromotion?.promotionOfferDescription ? (
    <div
      className="font-bold"
      // eslint-disable-next-line react/no-danger -- the rule was disabled before a comment became required
      dangerouslySetInnerHTML={{
        __html: offer.offerPromotion.promotionOfferDescription,
      }}
      data-qa-id="promotion-product-description"
    />
  ) : null
}
export const useProductPromotion = (): ProductPromotion => {
  const { currentOffer } = useProductPageContext()

  if (!currentOffer) {
    return {
      hasPromotion: false,
      promotionDescription: '',
      promotionFooter: '',
      promotionTitle: '',
      promotionType: undefined,
    }
  }

  const promotionType = productAccessorGetOfferPromotionType(currentOffer)

  return {
    hasPromotion: Boolean(currentOffer) && Boolean(promotionType),
    promotionDescription: getPromotionDescription(currentOffer),
    promotionFooter: promotionType ? getPromotionFooter() : null,
    promotionTitle: promotionType
      ? getPromotionTitle(currentOffer, promotionType)
      : null,
    promotionType,
  }
}
